import {Box, Flex, P, Span} from 'exsportia-components'
import React, {FC, useEffect, useMemo} from 'react'

import {SUBSCRIPTION_PRICE_TYPE_MAP} from '../../../api/subscriptions/constants'
import {useSubscription} from '../../../api/subscriptions/hooks'
import {Subscription, SubscriptionData} from '../../../api/subscriptions/types'
import {useVenue} from '../../../api/venue/hooks'
import {GradientLoader} from '../../../ui/components/gradient-loader/gradient-loader'
import {
  ItemProps,
  Option,
  RadioButtonOptionsList,
  Value,
} from '../../../ui/components/radio-button/radio-button-options-list'
import {useLocale} from '../../../utils/i18n'

type SelectedValue = Value<Subscription['prices'][number] | null>

type Props = {
  subscriptionId?: string
  select: (_plan: SubscriptionData['prices'][number] | null) => void
  selected: SelectedValue
  promoCode?: string
  firstCyclePrice?: number
}

const PriceLabel: FC<ItemProps<Subscription['prices'][number]>> = ({value}) => {
  const {l} = useLocale()
  const {data: venue} = useVenue()
  if (!venue) {
    return null
  }
  const listPrice = value.listPrice ?? value.initialPrice
  const shownPrice = value.firstPaymentPrice ?? value.price
  return (
    <Box>
      <P
        style={{
          textWrap: 'nowrap'
        }}
      >
        <Span>
          {`${l(SUBSCRIPTION_PRICE_TYPE_MAP[value.type].label)}, `}
        </Span>
        {listPrice !== undefined && listPrice !== shownPrice ? (
          <>
            <Span style={{textDecoration: 'line-through', color: '#8A8D94'}}>
              {l(`${venue.currency}_symbolic`, {value: listPrice})}
            </Span>
            <Span mx={'4px'}>{'→'}</Span>
            <Span color={'primary'}>
              {l(`${venue.currency}_symbolic`, {value: shownPrice})}
            </Span>
          </>
        ) : (
          <Span color={'primary'}>
            {l(`${venue.currency}_symbolic`, {value: value.price})}
          </Span>
        )}
      </P>
    </Box>
  )
}

const PriceItem: FC<ItemProps<Subscription['prices'][number]>> = ({children}) => (
  <Flex
    flexDirection={'row'}
  >
    <Box>
      {children}
    </Box>
  </Flex>
)

export const PaymentOptionsListSelect: FC<Props> = ({
  subscriptionId,
  select,
  selected,
  promoCode,
  firstCyclePrice,
}) => {
  const {data: subscription} = useSubscription(subscriptionId, {promoCode: promoCode || undefined, firstCyclePrice})
  // Re-select on every fetch, not only a new pass: applying or removing a promo code returns the
  // same pass with new prices, and the selected plan must carry them.
  useEffect(() => {
    if (subscription) {
      const sameType = selected ? subscription.prices.find((price) => price.type === selected.type) : undefined
      select(sameType || subscription.prices[0])
    }
  }, [subscription])
  const options= useMemo(() => {
    if (!subscription) {
      return []
    }
    return subscription.prices.map((price, i) => ({
      value: price,
      selected: selected ? selected.type === price.type : i === 0 || false,
      select: (value: SelectedValue) => select(value),
      disabled: false,
    })) as Option[]
  }, [subscription, selected])
  return (
    <Box mt={'16px'}>
      {
        !subscription
        ? (<GradientLoader height={'50px'}/>)
        : (
          <RadioButtonOptionsList
            options={options}
            itemComponent={PriceItem}
            labelComponent={PriceLabel}
          />
        )
      }
    </Box>
  )
}